import React, { Suspense, useEffect } from 'react';

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './components/Toast';
import { FaHourglassHalf, FaCreditCard, FaLock, FaChevronDown, FaBuilding } from 'react-icons/fa';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { WebSocketProvider } from './contexts/WebSocketContext';
import { UIProvider } from './contexts/UIContext';
import GlobalUIComponents from './components/GlobalUI/GlobalUIComponents';
// ChatWidget disabled — replaced by IMSChatBot
import AuthLayout from './components/AuthLayout';
import Navigation from './components/Navigation';
import IMSChatBot from './components/IMSChatBot';
import TourGuide from './components/TourGuide';
import ErrorBoundary from './components/ErrorBoundary';
import ConfirmModal from './components/ConfirmModal';
import ToastContainer from './components/Toast';
import ScanNotificationPrompt from './components/ScanNotificationPrompt';
import './styles/design-system.css';
import './App.css';

// ── Lazy-loaded pages (code-split into separate chunks) ──────────────────────
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const BarcodeGenerator = React.lazy(() => import('./pages/BarcodeGenerator'));
const Profile = React.lazy(() => import('./pages/Profile'));
const DeviceManager = React.lazy(() => import('./pages/DeviceManager'));
const IMSDashboard = React.lazy(() => import('./pages/IMSDashboard'));
const IMSCatalog = React.lazy(() => import('./pages/IMSCatalog'));
const IMSScanner = React.lazy(() => import('./pages/IMSScanner'));
const IMSSettings = React.lazy(() => import('./pages/IMSSettings'));
const IMSUsers = React.lazy(() => import('./pages/IMSUsers'));
const IMSWorkOrders = React.lazy(() => import('./pages/IMSWorkOrders'));
const IMSProduction = React.lazy(() => import('./pages/IMSProduction'));
const IMSLocations = React.lazy(() => import('./pages/IMSLocations'));
const IMSGrn = React.lazy(() => import('./pages/IMSGrn'));
const IMSReports = React.lazy(() => import('./pages/IMSReports'));
const WorkspaceOnboarding = React.lazy(() => import('./pages/WorkspaceOnboarding'));

// ── Page loading spinner (shown while lazy chunks load) ──────────────────────
const PageLoadingSpinner = () => (
  <div className="loading-container">
    <div className="loading-spinner">
      <div className="spinner"></div>
      <p>Loading...</p>
    </div>
  </div>
);

// Protected Route Component with Role-based Access Control
function ProtectedRoute({ children, requiredPath }) {
  const { isAuthenticated, hasPageAccess, isLoading, user } = useAuth();
  const { workspaces, loadingWorkspaces, workspacesLoaded, activeWorkspace } = useWorkspace();

  // Optimistic rendering: If authenticated (optimistically), render children even if loading.
  if (isLoading && !isAuthenticated()) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  // Force redirection to settings if subscription has expired

  // (We no longer redirect here so the page mounts and shows our beautiful floating modal overlay instead)


  // Show loading spinner if workspaces are loading or haven't finished loading yet
  if (requiredPath !== '/onboarding' && (!workspacesLoaded || (loadingWorkspaces && workspaces.length === 0))) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading workspaces...</p>
        </div>
      </div>
    );
  }

  // Redirect to onboarding if user has no workspaces
  if (requiredPath !== '/onboarding' && workspacesLoaded && workspaces.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  // Enforce workspace-level role restrictions for standard users
  const wsRole = activeWorkspace?.currentUserRole;
  if (wsRole === 'user' || wsRole === 'member' || wsRole === 'viewer') {
    const restrictedPaths = ['/ims-catalog', '/ims-settings', '/ims-users'];
    if (requiredPath && restrictedPaths.includes(requiredPath)) {
      return (
        <div className="access-denied" style={{ padding: '40px', textAlign: 'center', background: '#fff', borderRadius: '12px', margin: '40px auto', maxWidth: '600px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <h2 style={{ color: '#e74c3c', marginBottom: '16px' }}>Access Denied</h2>
          <p style={{ color: '#7f8c8d', fontSize: '16px', marginBottom: '24px' }}>You do not have permission to access this page.</p>
          <p style={{ color: '#95a5a6', fontSize: '14px', marginBottom: '24px' }}>Your workspace role does not include access to this feature.</p>
          <button className="btn btn-primary" onClick={() => window.history.back()}>
            Go Back
          </button>
        </div>
      );
    }
  }

  // Removed forced redirect to onboarding so users go straight to dashboard upon login
  if (requiredPath && requiredPath !== '/onboarding' && !hasPageAccess(requiredPath)) {
    return (
      <div className="access-denied">
        <div className="access-denied-content">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
          <p>Your role doesn't include access to this feature.</p>
          <button onClick={() => window.history.back()}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// Subscription required overlay modal
const SubscriptionModal = () => {
  const { imsFetch, activeWorkspaceId, activeWorkspace, workspaces, switchWorkspace } = useWorkspace();
  const { getUserInfo, logout } = useAuth();
  const showToast = useToast();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const currentUser = getUserInfo();

  const wsRole = activeWorkspace?.currentUserRole;
  const isWorkspaceAdmin = ['owner', 'admin'].includes(wsRole);
  
  // All active workspaces that the user belongs to
  const allUserWorkspaces = workspaces;

  // Pricing calculations based on configured user limit
  const configuredLimit = activeWorkspace?.userLimit && activeWorkspace.userLimit >= 10 ? Number(activeWorkspace.userLimit) : 10;
  const additionalBlocks = Math.ceil((configuredLimit - 10) / 5);
  const planPrice = 500 + additionalBlocks * 200;

  React.useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const loadRazorpayScript = () => {
    if (window.Razorpay) return Promise.resolve(true);
    return new Promise((resolve) => {
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        existingScript.addEventListener('load', () => resolve(true), { once: true });
        existingScript.addEventListener('error', () => resolve(false), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayAndActivate = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const scriptReady = await loadRazorpayScript();
      if (!scriptReady) {
        showToast('Failed to load Razorpay checkout', 'error');
        setIsProcessing(false);
        return;
      }

      const orderRes = await imsFetch('/api/ims/payments/razorpay/order', {
        method: 'POST',
        body: JSON.stringify({ targetUserLimit: configuredLimit })
      });
      const orderData = await orderRes.json();
      if (!orderData.success) {
        showToast(orderData.error || 'Failed to create payment order', 'error');
        setIsProcessing(false);
        return;
      }

      const options = {
        key: orderData.keyId,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'RoBridge Labs',
        description: `Renew subscription plan (${configuredLimit} users)`,
        order_id: orderData.order.id,
        prefill: {
          name: currentUser?.name || '',
          email: currentUser?.email || ''
        },
        notes: {
          workspace_id: activeWorkspaceId,
          target_user_limit: String(configuredLimit)
        },
        theme: { color: '#E3821E' },
        modal: {
          ondismiss: () => {
            setIsProcessing(false);
            showToast('Payment cancelled', 'info');
          }
        },
        handler: async (response) => {
          try {
            const verifyRes = await imsFetch('/api/ims/payments/razorpay/verify', {
              method: 'POST',
              body: JSON.stringify({
                ...response,
                targetUserLimit: configuredLimit
              })
            });
            const verifyData = await verifyRes.json();
            if (verifyData.success) {
              showToast('Payment verified successfully. Workspace activated!', 'success');
              setTimeout(() => {
                window.location.reload();
              }, 1500);
            } else {
              showToast(verifyData.error || 'Payment verification failed', 'error');
              setIsProcessing(false);
            }
          } catch (err) {
            console.error(err);
            showToast('Payment verification failed', 'error');
            setIsProcessing(false);
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        console.error('Razorpay payment failed', response?.error);
        showToast(response?.error?.description || 'Payment failed', 'error');
        setIsProcessing(false);
      });
      razorpay.open();
    } catch (err) {
      console.error(err);
      showToast('Upgrade payment failed', 'error');
      setIsProcessing(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isSubscriptionExpired = activeWorkspace?.subscriptionReason === 'subscription_expired';

  return (
    <div className="subscription-overlay-modal-backdrop">
      <div className="subscription-overlay-modal-card">
        {isWorkspaceAdmin ? (
          <>
            <div className="overlay-modal-header">
              <div className="overlay-warning-icon">
                <FaHourglassHalf />
              </div>
              <h2>{isSubscriptionExpired ? 'Subscription Expired' : 'Trial Expired'}</h2>
            </div>
            <div className="overlay-modal-body">
              <p>
                {isSubscriptionExpired ? (
                  "Your workspace subscription has expired. Please renew your subscription to unlock full access to the RoBridge IMS platform."
                ) : (
                  "Your workspace free trial has expired. Please subscribe to a plan to unlock full access to the RoBridge IMS platform."
                )}
              </p>
              <div className="overlay-price-preview">
                <span>{configuredLimit === 10 ? 'Base Plan' : 'Custom Plan'} ({configuredLimit} seats):</span>
                <strong>Rs. {planPrice} <span className="period">/ month</span></strong>
              </div>
            </div>
            <div className="overlay-modal-footer">
              <button 
                className="btn btn-primary btn-pay-activate" 
                onClick={handlePayAndActivate}
                disabled={isProcessing}
              >
                <FaCreditCard style={{ marginRight: '8px' }} />
                {isProcessing ? 'Processing...' : (isSubscriptionExpired ? 'Pay & Renew Now' : 'Pay & Activate Now')}
              </button>
              <button 
                type="button"
                className="overlay-goto-settings-link" 
                onClick={() => navigate('/ims-settings')}
                style={{ background: 'none', border: 'none', color: '#3498db', textDecoration: 'underline', cursor: 'pointer', margin: '8px 0', fontSize: '14px' }}
              >
                Configure upgrade parameters in Settings
              </button>
              <button 
                className="btn btn-outline-primary" 
                onClick={() => navigate('/onboarding')} 
                style={{ marginTop: '8px', width: '100%', borderColor: '#E3821E', color: '#E3821E' }}
              >
                Go to Onboarding Page
              </button>
              <button className="btn btn-outline-secondary" onClick={handleLogout} style={{ marginTop: '8px', width: '100%' }}>
                Log Out
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="overlay-modal-header">
              <div className="overlay-warning-icon" style={{ background: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c' }}>
                <FaLock />
              </div>
              <h2>{isSubscriptionExpired ? 'Subscription Expired' : 'Trial Expired'}</h2>
            </div>
            <div className="overlay-modal-body">
              <p>
                {isSubscriptionExpired ? (
                  `The subscription period for workspace "${activeWorkspace?.name}" has expired. Please contact the workspace Owner or Administrator to renew the subscription and restore access.`
                ) : (
                  `The trial period for workspace "${activeWorkspace?.name}" has ended. Please contact the workspace Owner or Administrator to make a payment and restore access.`
                )}
              </p>
            </div>
            <div className="overlay-modal-footer">
              {allUserWorkspaces.length > 1 ? (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative' }} ref={dropdownRef}>
                  <label style={{ fontSize: '13px', color: '#7f8c8d', textAlign: 'left', fontWeight: '600' }}>Switch to another active workspace:</label>
                  <div 
                    className="custom-dropdown-trigger" 
                    onClick={() => setIsOpen(!isOpen)}
                  >
                    <FaBuilding style={{ color: '#E3821E', fontSize: '16px' }} />
                    <span className="dropdown-trigger-text" style={{ color: '#2c3e50', fontWeight: '600' }}>
                      {activeWorkspace ? activeWorkspace.name : 'Select Workspace...'}
                    </span>
                    <FaChevronDown className={`dropdown-trigger-chevron ${isOpen ? 'open' : ''}`} />
                  </div>
                  {isOpen && (
                    <ul className="custom-dropdown-options-list">
                      {allUserWorkspaces.map(ws => {
                        const isActive = String(ws.id) === String(activeWorkspaceId);
                        return (
                          <li 
                            key={ws.id} 
                            className={`custom-dropdown-option-item ${isActive ? 'current-active' : ''}`}
                            onClick={() => {
                              if (isActive) return;
                              switchWorkspace(ws.id);
                              setIsOpen(false);
                            }}
                            style={isActive ? { cursor: 'default', opacity: 0.8 } : {}}
                          >
                            <FaBuilding />
                            <span style={{ flex: 1, textAlign: 'left' }}>{ws.name}</span>
                            {isActive && (
                              <span className="current-workspace-badge">Current Workspace</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              ) : null}
              <button 
                className="btn btn-outline-primary" 
                onClick={() => navigate('/onboarding')} 
                style={{ marginTop: '8px', width: '100%', borderColor: '#E3821E', color: '#E3821E' }}
              >
                Go to Onboarding Page
              </button>
              <button className="btn btn-primary" onClick={handleLogout} style={{ width: '100%', marginTop: '10px' }}>
                Log Out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Main App Content Component
function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const { workspaces, loadingWorkspaces, workspacesLoaded, activeWorkspace } = useWorkspace();
  const location = useLocation();


  // Automatically find labels ending with "*" and style the "*" as orange globally
  useEffect(() => {
    const updateLabels = () => {
      const labels = document.querySelectorAll('label, .form-label, .label, .ims-form-label');
      labels.forEach(label => {
        // Skip if label has no text, or already has wrapped asterisk, or contains input tags
        if (!label.innerText || label.querySelector('.required-asterisk') || label.querySelector('input')) return;
        
        // Find text nodes inside this label that contain '*'
        const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
        let textNode;
        while ((textNode = walker.nextNode())) {
          const val = textNode.nodeValue;
          if (val.includes('*')) {
            const parent = textNode.parentNode;
            if (parent) {
              const parts = val.split('*');
              
              // Set the text value of node to everything before '*'
              textNode.nodeValue = parts[0];
              
              // Create the orange asterisk span
              const span = document.createElement('span');
              span.className = 'required-asterisk';
              span.innerText = '*';
              
              // Insert span after this text node
              parent.insertBefore(span, textNode.nextSibling);
              
              // If there was text after '*', create a new text node for it
              if (parts[1]) {
                const tailNode = document.createTextNode(parts[1]);
                parent.insertBefore(tailNode, span.nextSibling);
              }
            }
            break; // Stop walking once we process the asterisk in this label
          }
        }
      });
    };
    
    // Initial run
    updateLabels();
    
    // MutationObserver to automatically apply to dynamically loaded components & route changes
    const observer = new MutationObserver(updateLabels);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Automatically find labels ending with "*" and style the "*" as orange globally
  useEffect(() => {
    const updateLabels = () => {
      const labels = document.querySelectorAll('label, .form-label, .label, .ims-form-label');
      labels.forEach(label => {
        // Skip if label has no text, or already has wrapped asterisk, or contains input tags
        if (!label.innerText || label.querySelector('.required-asterisk') || label.querySelector('input')) return;
        
        // Find text nodes inside this label that contain '*'
        const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT);
        let textNode;
        while (textNode = walker.nextNode()) {
          const val = textNode.nodeValue;
          if (val.includes('*')) {
            const parent = textNode.parentNode;
            if (parent) {
              const parts = val.split('*');
              
              // Set the text value of node to everything before '*'
              textNode.nodeValue = parts[0];
              
              // Create the orange asterisk span
              const span = document.createElement('span');
              span.className = 'required-asterisk';
              span.innerText = '*';
              
              // Insert span after this text node
              parent.insertBefore(span, textNode.nextSibling);
              
              // If there was text after '*', create a new text node for it
              if (parts[1]) {
                const tailNode = document.createTextNode(parts[1]);
                parent.insertBefore(tailNode, span.nextSibling);
              }
            }
            break; // Stop walking once we process the asterisk in this label
          }
        }
      });
    };
    
    // Initial run
    updateLabels();
    
    // MutationObserver to automatically apply to dynamically loaded components & route changes
    const observer = new MutationObserver(updateLabels);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Show loading spinner only if we don't have a user session yet
  // If we have a user (optimistic load), we render the app immediately while verifying in background
  if (isLoading && !isAuthenticated()) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading RobBridge...</p>
        </div>
      </div>
    );
  }

  // Show login/signup page if not authenticated
  if (!isAuthenticated()) {
    return (
      <Suspense fallback={<PageLoadingSpinner />}>
        <Routes>
          <Route element={<AuthLayout />}>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="*" element={<LoginPage />} />
          </Route>
        </Routes>
      </Suspense>
    );
  }

  // If workspaces haven't finished loading yet, show a full-screen loading spinner.
  // We do not check loadingWorkspaces here to prevent unmounting the onboarding page 
  // when a user is creating their first workspace.
  if (!workspacesLoaded) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading workspaces...</p>
        </div>
      </div>
    );
  }

  // Helper component to redirect authenticated users away from login/signup pages cleanly
  const AuthRedirect = () => {
    if (!workspacesLoaded || loadingWorkspaces) {
      return (
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading workspaces...</p>
          </div>
        </div>
      );
    }
    if (workspaces.length === 0) {
      return <Navigate to="/onboarding" replace />;
    }
    return <Navigate to="/" replace />;
  };

  // Show main application if authenticated
  return (
    <Routes>
      {/* ── Redirect auth routes for authenticated users to avoid layout flash ── */}
      <Route path="/signup" element={<AuthRedirect />} />
      <Route path="/login" element={<AuthRedirect />} />
      <Route path="/verify-email" element={<AuthRedirect />} />
      {/* ── Onboarding: full-screen, no sidebar ── */}
      <Route path="/onboarding" element={
        <Suspense fallback={<PageLoadingSpinner />}>
          <ProtectedRoute requiredPath="/onboarding">
            <WorkspaceOnboarding />
          </ProtectedRoute>
        </Suspense>
      } />

      {/* ── All other pages: inside the App shell with sidebar, or redirect to onboarding if no workspaces ── */}
      <Route path="*" element={
        workspaces.length === 0 ? (
          <Navigate to="/onboarding" replace />
        ) : (
          <div className="App">
            <Navigation />
            <main className="app-main-content">
              <Suspense fallback={<PageLoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<ProtectedRoute requiredPath="/"><IMSDashboard /></ProtectedRoute>} />
                  <Route path="/dashboard" element={<ProtectedRoute requiredPath="/"><IMSDashboard /></ProtectedRoute>} />
                  <Route path="/generator" element={<ProtectedRoute requiredPath="/generator"><BarcodeGenerator /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute requiredPath="/profile"><Profile /></ProtectedRoute>} />
                  <Route path="/device-manager" element={<ProtectedRoute requiredPath="/device-manager"><DeviceManager /></ProtectedRoute>} />
                  <Route path="/ims-catalog" element={<ProtectedRoute requiredPath="/ims-catalog"><IMSCatalog /></ProtectedRoute>} />
                  <Route path="/ims-scanner" element={<ProtectedRoute requiredPath="/ims-scanner"><IMSScanner /></ProtectedRoute>} />
                  <Route path="/ims-settings" element={<ProtectedRoute requiredPath="/ims-settings"><IMSSettings /></ProtectedRoute>} />
                  <Route path="/ims-users" element={<ProtectedRoute requiredPath="/ims-users"><IMSUsers /></ProtectedRoute>} />
                  <Route path="/ims-workorders" element={<ProtectedRoute requiredPath="/ims-workorders"><IMSWorkOrders /></ProtectedRoute>} />
                  <Route path="/ims-production" element={<ProtectedRoute requiredPath="/ims-production"><IMSProduction /></ProtectedRoute>} />
                  <Route path="/ims-locations" element={<ProtectedRoute requiredPath="/ims-locations"><IMSLocations /></ProtectedRoute>} />
                  <Route path="/ims-grn" element={<ProtectedRoute requiredPath="/ims-grn"><IMSGrn /></ProtectedRoute>} />
                  <Route path="/ims-reports" element={<ProtectedRoute requiredPath="/ims-reports"><IMSReports /></ProtectedRoute>} />
                  <Route path="/login" element={<LoginPage />} />
                </Routes>
              </Suspense>
            </main>
            <TourGuide />
            <IMSChatBot />
            <ScanNotificationPrompt />

            {activeWorkspace && activeWorkspace.requiresSubscription && !['/ims-settings', '/profile', '/onboarding'].includes(location.pathname.replace(/^\/bvs/, '').replace(/\/$/, '') || '/') && (
              <SubscriptionModal />
            )}

          </div>
        )
      } />
    </Routes>
  );
}

// Main App Component with Auth Provider
// In development, handle both root and /bvs paths (for testing)
// In production, strictly use /bvs as per deployment requirement
const routerBasename = process.env.NODE_ENV === 'production' ? '/bvs' : '/';

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <WorkspaceProvider>
          <WebSocketProvider>
            <UIProvider>
              <Router basename={routerBasename}>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
                <GlobalUIComponents />
                <ConfirmModal />
                <ToastContainer />
              </Router>
            </UIProvider>
          </WebSocketProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
